<script lang="ts">
	import type {
		Store,
		Module,
		SearchField,
		SearchModule,
		StaticSearchField,
		StaticSearchFieldKey,
		AdditionalField as AdditionalFieldType,
		InventoryStaticSearchFieldValue,
		InventoryQuestionValue,
		AdditionalFieldValue,
		ParsedStateParameters,
	} from 'utility/search-fields'
	import type { ActivityInput } from '@isoftdata/svelte-sidebar'
	import type { Mediator, SvelteAsr } from 'types/common'

	import Icon from '@isoftdata/svelte-icon'
	import Button from '@isoftdata/svelte-button'
	import AdditionalField from './AdditionalField.svelte'
	import StoreSelect from 'components/search/StoreSelect.svelte'
	import SideAutocomplete from './SideAutocomplete.svelte'
	import SmartSearchInput from './SmartSearchInput.svelte'
	import StatusSelect from 'components/search/StatusSelect.svelte'
	import TextField from 'components/search/InputSearchField.svelte'
	import ModelAutocomplete from 'components/search/SearchFieldModel.svelte'
	import YearRangeInput from 'components/search/YearRangeInput.svelte'
	import LocationAutocomplete from 'components/search/LocationAutocomplete.svelte'
	import ConditionAutocomplete from './ConditionAutocomplete.svelte'
	import ManufacturerAutocomplete from 'components/search/SearchFieldManufacturer.svelte'
	import AddEditSearchFilters from 'components/search/AddEditSearchFilters.svelte'
	import CategoryAutocomplete from 'components/search/SearchFieldCategory.svelte'
	import InventoryQuestionField from 'components/search/InventoryQuestionField.svelte'
	import UserStatusAutocomplete from 'components/search/UserStatusAutocomplete.svelte'
	import InventoryTypeAutocomplete from 'components/search/SearchFieldInventoryType.svelte'
	import NullableRadioCheckbox from 'components/NullableRadioCheckbox.svelte'
	import NumberRange from './NumberRange.svelte'
	import {
		loadInventoryQuestions,
		getStaticSearchFieldsWithDependencyGraph,
		getInitialSelectedSearchFields,
		getSearchFieldsByCategory,
		getValidTypeFields,
		saveUserSetting,
		computeSearchParameterValues,
		getParsedQAFields,
		getParsedAdditionalFields,
		getParsedTypeFields,
	} from 'utility/search-fields'
	import EditModeControls from 'components/search/EditModeControls.svelte'
	import Pin from 'components/search/Pin.svelte'
	import session from 'stores/session'
	import { getContext, onMount } from 'svelte'
	import { SvelteSet } from 'svelte/reactivity'
	import { klona } from 'klona'
	import { PersistedUserState } from '@isoftdata/svelte-store-user-local-writable'
	import type { i18n } from 'types/common'
	const { t: translate } = getContext<i18n>('i18next')

	const lastInventorySearch = new PersistedUserState<Record<string, unknown>>(
		$session.userAccountId,
		'lastInventorySearch',
		{ defaultValue: {} },
	)
	const mediator = getContext<Mediator>('mediator')

	interface Props {
		// #region props
		parsedParameters: ParsedStateParameters
		searchModules?: SearchModule[]
		searchableStores?: Store[]
		stores?: number[]
		searchableInventoryFields?: AdditionalFieldType[]
		asr: SvelteAsr
		userPinnedSearchFields?: string[]
	}

	let {
		parsedParameters,
		searchModules = [],
		searchableStores = [],
		stores = [],
		searchableInventoryFields = [],
		asr,
		userPinnedSearchFields = [],
	}: Props = $props()
	// #endregion

	// #region local vars
	/** List of all static search fields, with their dependencies. (All possible search fields) */
	const staticSearchFields = getStaticSearchFieldsWithDependencyGraph()
	const staticSearchFieldsMap: Map<StaticSearchFieldKey, StaticSearchField> = new Map(
		staticSearchFields.map(field => [field.key, field]),
	)

	/** List of selected search fields */
	let selectedSearchFieldsKeys = $derived(
		getInitialSelectedSearchFields(staticSearchFields, userPinnedSearchFields, parsedParameters),
	)
	let inEditMode: boolean = $state(false)
	let inventoryQuestions: SearchField[] = $state([])
	/** Track if the user has run a search, so we can replace the search activity if they do another */
	let hasSearched = $state(false)
	const pinnedSearchFields: SvelteSet<string> = new SvelteSet()

	// #region maps of search fields with values
	let staticSearchFieldValues: InventoryStaticSearchFieldValue = $state({
		...klona(getDefaultStaticSearchFieldValues()),
		...getStaticSearchFieldValueWithParsedParams(),
	})
	let additionalFieldValues: Record<string, AdditionalFieldValue> = $derived(
		getParsedAdditionalFields(parsedParameters),
	)
	let typeFieldValues: Record<string, string> = $state({}) //Type fields can only be strings()
	let inventoryQuestionValues: Record<string, InventoryQuestionValue> = $derived(getParsedQAFields(parsedParameters))
	// #endregion

	const typeFields: SearchField[] = $derived(getValidTypeFields(staticSearchFieldValues.INVENTORY_TYPE))
	const additionalSearchFields: SearchField[] = $derived(
		getAdditionalFields(searchableInventoryFields, staticSearchFields),
	)

	/** List of all search fields. */
	const searchFields: SearchField[] = $derived([
		...staticSearchFields,
		...typeFields,
		...inventoryQuestions,
		...additionalSearchFields,
	])

	const searchFieldsByCategory = $derived(
		getSearchFieldsByCategory(staticSearchFields, typeFields, inventoryQuestions, additionalSearchFields),
	)

	/** List of search field values that will be passed to Result state */
	const searchParameterValues = $derived(
		computeSearchParameterValues(
			selectedSearchFieldsKeys,
			staticSearchFieldValues,
			additionalFieldValues,
			typeFieldValues,
			inventoryQuestionValues,
			staticSearchFieldsMap,
		),
	)

	$effect(() => {
		loadAndSetInventoryQuestions({
			inventoryTypeId: staticSearchFieldValues.INVENTORY_TYPE?.id,
			manufacturerId: staticSearchFieldValues.PART_MANUFACTURER?.id,
			modelId: staticSearchFieldValues.PART_MODEL?.id,
			category: staticSearchFieldValues.CATEGORY?.name,
		})
	})

	function getDefaultStaticSearchFieldValues() {
		return Object.freeze<InventoryStaticSearchFieldValue>({
			STORE: stores, //default to their current store, passed from part-search.ts
			INVENTORY_TYPE: null,
			ASSEMBLY_MANUFACTURER: null,
			ASSEMBLY_MODEL: null,
			PART_MANUFACTURER: null,
			PART_MODEL: null,
			LOCATION: null,
			CONDITION: null,
			STATUS: '@',
			USER_STATUS: null,
			CATEGORY: null,
			SIDE: null,
			TAG_NUMBER: '',
			YEAR_RANGE: { from: null, to: null },
			VEHICLE_MANUFACTURER: null,
			VEHICLE_MODEL: null,
			HAS_PUBLIC_IMAGES: null,
			IMAGE_COUNT: { from: null, to: null },
			SMART_SEARCH: {
				keyword: '',
				moduleIds: searchModules.reduce((acc: Array<Module['id']>, searchModule) => {
					return searchModule.default ? [...acc, searchModule.module.id] : acc
				}, []), //This is the user's default search modules
			},
		})
	}

	function getStaticSearchFieldValueWithParsedParams() {
		return {
			TAG_NUMBER: parsedParameters.TAG_NUMBER ?? '',
			STATUS: parsedParameters.STATUS ?? '@',
			CONDITION: parsedParameters.CONDITION ?? null,
			YEAR_RANGE: parsedParameters.YEAR_RANGE ?? { from: null, to: null },
			IMAGE_COUNT: parsedParameters.IMAGE_COUNT ?? { from: null, to: null },
			HAS_PUBLIC_IMAGES: parsedParameters.HAS_PUBLIC_IMAGES ?? null,
		}
	}

	async function loadAndSetInventoryQuestions({
		inventoryTypeId,
		manufacturerId,
		modelId,
		category,
	}: {
		inventoryTypeId?: number
		manufacturerId?: number
		modelId?: number
		category?: string | null
	}) {
		const loadedInventoryQuestions = await loadInventoryQuestions({
			inventoryTypeId,
			manufacturerId,
			modelId,
			category: category,
		})
		let inventoryQuestionNames = new Set()
		inventoryQuestions = loadedInventoryQuestions.reduce((acc: SearchField[], question) => {
			//Maybe the API should be ensuring that we don't get multiple questions with the same name, but as of writing this comment(2024-04-10), it doesn't.
			if (inventoryQuestionNames.has(question.name)) {
				return acc
			} else {
				inventoryQuestionNames.add(question.name)
				return [
					...acc,
					{
						key: `QA_${question.name}`, //we use name as the key because id is specific to a type/man/model/category combo, but we want to be able to use the same question across different combinations
						name: question.name,
						dependencies: [],
						qaFieldData: question,
					},
				]
			}
		}, [])
	}

	function getAdditionalFields(
		inventorySearchFields: AdditionalFieldType[],
		staticSearchFields: StaticSearchField[],
	): SearchField[] {
		//We don't want duplicates of our nice static search fields, so we'll filter out any of the dynamic "additional" fields that have the same dbFieldKey as any of the static fields.
		const searchFieldsToExclude = staticSearchFields.reduce((acc: string[], field) => {
			if (field.dbFieldKey?.length) {
				acc = [...acc, ...field.dbFieldKey]
				return acc
			} else return acc
		}, [])

		return inventorySearchFields.reduce((acc: SearchField[], additionalField) => {
			//Nested looping, amirite? But since we actually need to look in the contents of the string, we can't use a Set or even an .includes(), unfortunately.
			if (
				!!searchFieldsToExclude.find(
					searchFieldToExclude => additionalField.fieldName.indexOf(searchFieldToExclude) > -1,
				)
			) {
				return acc
			}

			return [
				...acc,
				{
					key: `ADDITIONAL_${additionalField.fieldName}`, //Because these fields are dynamic just come from a table in the db, We've decided to key off the field name, rather than the auto-increment id.
					name: `${additionalField.displayName}`,
					dependencies: [],
					additionalFieldData: additionalField,
				},
			]
		}, [])
	}

	async function toggleEditMode(forceExit = false) {
		inEditMode = forceExit ? false : !inEditMode

		if (!inEditMode) {
			const searchFieldsSet = new Set(searchFields.map(field => field.key))
			await saveUserSetting.mutate({
				input: {
					searchParts: {
						//Just in case, filter out any invalid keys
						pinnedSearchFields: Array.from(pinnedSearchFields).filter(field => searchFieldsSet.has(field)),
					},
				},
			})
		}
	}

	function doSearch() {
		toggleEditMode(true)
		lastInventorySearch.current = searchParameterValues
		if (!staticSearchFieldValues.STORE.length) {
			mediator.publish('showMessage', {
				heading: translate('vehicleSearch.noStoreSelected', 'No Store Selected'),
				message: translate('partSearch.pleaseSelectAStoreToSearchParts', 'Please select a store to search parts'),
				color: 'danger',
				time: false,
			})
			return
		}
		logActivity()
		asr.go('app.part-search.results', searchParameterValues)
	}

	function logActivity() {
		const activity: ActivityInput = {
			icon: 'engine',
			title: translate('vehicleSearch.mostRecentSearch', 'Most Recent Search'),
			type: 'PART',
			route: 'app.part-search.results',
			name: 'Most Recent Search',
			// @ts-expect-error
			parameters: searchParameterValues,
		}
		if (hasSearched) {
			mediator.publish('replaceActivity', activity)
		} else {
			mediator.publish('activity', activity)
			hasSearched = true
		}
	}

	onMount(() => {
		userPinnedSearchFields.forEach(field => pinnedSearchFields.add(field))
		typeFieldValues = getParsedTypeFields(parsedParameters)
	})
</script>

<div class="card">
	<div class="card-header">
		<div class="d-flex justify-content-between align-items-end">
			<div class="h4 mb-0">
				<div>
					<Icon
						icon="engine"
						class="mr-2"
					/><span class="d-none d-sm-inline-block">{translate('partSearch.partSearch', 'Part Search')}</span>
				</div>
			</div>
			<div>
				<!-- If they're not looking at results, allow them to restore their previous search with a single button click -->
				{#if asr.getActiveState().name === 'app.part-search' && lastInventorySearch.current}
					<Button
						size="sm"
						outline
						icon="history"
						class="mr-2"
						href={asr.makePath('app.part-search.results', lastInventorySearch.current)}
					>
						{translate('vehicleSearch.restoreLastSearch', 'Restore Last Search')}
					</Button>
				{/if}
				<Button
					id="newPart"
					href={asr.makePath('app.part')}
					size="sm"
					outline
					color="success"
					iconClass="plus"
				>
					{translate('common:newPart', 'New Part')}
				</Button>
			</div>
		</div>
	</div>
	<div class="card-body">
		<form
			id="search-field-form"
			onsubmit={e => {
				e.preventDefault()
				doSearch()
			}}
		>
			{#if selectedSearchFieldsKeys.size}
				<div class="form-row row-cols-2 align-items-end">
					{#each selectedSearchFieldsKeys as searchFieldKey (searchFieldKey)}
						{@const searchField = searchFields.find(field => field.key === searchFieldKey)}
						{#if searchField}
							{#if searchField?.key === 'STORE'}
								<StoreSelect
									id={searchField?.key}
									{searchField}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									stores={searchableStores}
									initialValue={parsedParameters.STORE}
									bind:selectedStores={staticSearchFieldValues.STORE}
								/>
							{:else if searchField?.key === 'INVENTORY_TYPE'}
								<InventoryTypeAutocomplete
									id={searchField?.key}
									{searchField}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									vehicle={false}
									{inEditMode}
									initialValue={parsedParameters.INVENTORY_TYPE
										? parseInt(parsedParameters.INVENTORY_TYPE, 10)
										: undefined}
									bind:inventoryType={staticSearchFieldValues.INVENTORY_TYPE}
								/>
							{:else if searchField?.key === 'ASSEMBLY_MANUFACTURER'}
								<ManufacturerAutocomplete
									id={searchField?.key}
									{searchField}
									type="ASSEMBLY"
									inventoryType={staticSearchFieldValues.INVENTORY_TYPE}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									initialValue={parsedParameters.ASSEMBLY_MANUFACTURER
										? parseInt(parsedParameters.ASSEMBLY_MANUFACTURER, 10)
										: undefined}
									bind:manufacturer={staticSearchFieldValues.ASSEMBLY_MANUFACTURER}
								/>
							{:else if searchField?.key === 'ASSEMBLY_MODEL'}
								<ModelAutocomplete
									id={searchField?.key}
									{searchField}
									type="ASSEMBLY"
									inventoryType={staticSearchFieldValues.INVENTORY_TYPE}
									manufacturer={staticSearchFieldValues.ASSEMBLY_MANUFACTURER}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									initialValue={parsedParameters.ASSEMBLY_MODEL
										? parseInt(parsedParameters.ASSEMBLY_MODEL, 10)
										: undefined}
									bind:model={staticSearchFieldValues.ASSEMBLY_MODEL}
								/>
							{:else if searchField?.key === 'PART_MANUFACTURER'}
								<ManufacturerAutocomplete
									id={searchField?.key}
									{searchField}
									type="PART"
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									initialValue={parsedParameters.PART_MANUFACTURER
										? parseInt(parsedParameters.PART_MANUFACTURER, 10)
										: undefined}
									inventoryType={staticSearchFieldValues.INVENTORY_TYPE}
									bind:manufacturer={staticSearchFieldValues.PART_MANUFACTURER}
								/>
							{:else if searchField?.key === 'PART_MODEL'}
								<ModelAutocomplete
									id={searchField?.key}
									{searchField}
									type="PART"
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									initialValue={parsedParameters.PART_MODEL ? parseInt(parsedParameters.PART_MODEL, 10) : undefined}
									inventoryType={staticSearchFieldValues.INVENTORY_TYPE}
									manufacturer={staticSearchFieldValues.PART_MANUFACTURER}
									bind:model={staticSearchFieldValues.PART_MODEL}
								/>
							{:else if searchField?.key === 'LOCATION'}
								<LocationAutocomplete
									id={searchField?.key}
									{searchField}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									stores={staticSearchFieldValues.STORE}
									initialValue={parsedParameters.LOCATION}
									bind:inventoryLocation={staticSearchFieldValues.LOCATION}
								/>
							{:else if searchField?.key === 'CONDITION'}
								<ConditionAutocomplete
									id={searchField?.key}
									{searchField}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									bind:condition={staticSearchFieldValues.CONDITION}
								/>
							{:else if searchField?.key === 'STATUS'}
								<StatusSelect
									id={searchField?.key}
									{searchField}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									bind:status={staticSearchFieldValues.STATUS}
								/>
							{:else if searchField?.key === 'USER_STATUS'}
								<UserStatusAutocomplete
									id={searchField?.key}
									{searchField}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									initialValue={parsedParameters.USER_STATUS}
									bind:userStatus={staticSearchFieldValues.USER_STATUS}
								/>
							{:else if searchField?.key === 'CATEGORY'}
								<CategoryAutocomplete
									id={searchField?.key}
									{searchField}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									inventoryType={staticSearchFieldValues.INVENTORY_TYPE}
									initialValue={parsedParameters.CATEGORY}
									bind:category={staticSearchFieldValues.CATEGORY}
								/>
							{:else if searchField?.key === 'SIDE'}
								<SideAutocomplete
									id={searchField?.key}
									{searchField}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									initialValue={parsedParameters.SIDE}
									bind:side={staticSearchFieldValues.SIDE}
								/>
							{:else if searchField?.key === 'YEAR_RANGE'}
								<YearRangeInput
									id={searchField?.key}
									label="Year"
									{searchField}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									bind:value={staticSearchFieldValues.YEAR_RANGE}
								/>
							{:else if searchField?.key === 'VEHICLE_MANUFACTURER'}
								<ManufacturerAutocomplete
									id={searchField?.key}
									{searchField}
									type="VEHICLE"
									inventoryType={staticSearchFieldValues.INVENTORY_TYPE}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									initialValue={parsedParameters.VEHICLE_MANUFACTURER
										? parseInt(parsedParameters.VEHICLE_MANUFACTURER, 10)
										: undefined}
									bind:manufacturer={staticSearchFieldValues.VEHICLE_MANUFACTURER}
								/>
							{:else if searchField?.key === 'VEHICLE_MODEL'}
								<ModelAutocomplete
									id={searchField?.key}
									type="VEHICLE"
									inventoryType={staticSearchFieldValues.INVENTORY_TYPE}
									manufacturer={staticSearchFieldValues.VEHICLE_MANUFACTURER}
									{searchField}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									{inEditMode}
									initialValue={parsedParameters.VEHICLE_MODEL
										? parseInt(parsedParameters.VEHICLE_MODEL, 10)
										: undefined}
									bind:model={staticSearchFieldValues.VEHICLE_MODEL}
								/>
							{:else if searchField?.key === 'SMART_SEARCH'}
								<SmartSearchInput
									id={searchField?.key}
									{searchField}
									{searchModules}
									{inEditMode}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									initialValue={parsedParameters.SMART_SEARCH}
									bind:keyword={staticSearchFieldValues.SMART_SEARCH.keyword}
									bind:selectedSearchModuleIds={staticSearchFieldValues.SMART_SEARCH.moduleIds}
								/>
							{:else if searchField.key.startsWith('ADDITIONAL_')}
								<AdditionalField
									id={searchField?.key}
									{searchField}
									{inEditMode}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									value={additionalFieldValues[searchField.key]}
									change={ctx => (additionalFieldValues[searchField.key] = ctx.value)}
								/>
							{:else if searchField.key.startsWith('QA_')}
								<InventoryQuestionField
									id={searchField?.key}
									{searchField}
									{inEditMode}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									value={inventoryQuestionValues[searchField.key]}
									change={ctx => (inventoryQuestionValues[searchField.key] = ctx.value)}
								/>
							{:else if searchField.key.startsWith('TYPE_FIELD_')}
								<TextField
									id={searchField?.key}
									{searchField}
									{inEditMode}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									value={typeFieldValues[searchField.key]}
									change={ctx => (typeFieldValues[searchField.key] = ctx.value)}
								/>
							{:else if searchField.key === 'TAG_NUMBER'}
								<TextField
									id={searchField?.key}
									{searchField}
									{inEditMode}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
									value={staticSearchFieldValues[searchField.key]}
									change={ctx => (staticSearchFieldValues[searchField.key] = ctx.value)}
								/>
							{:else if searchField.key === 'IMAGE_COUNT'}
								<EditModeControls
									{searchField}
									{inEditMode}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
								>
									<NumberRange
										id={searchField?.key}
										label={searchField.name}
										bind:from={
											() => staticSearchFieldValues.IMAGE_COUNT?.from,
											from => {
												staticSearchFieldValues.IMAGE_COUNT ??= { from: null, to: null }
												staticSearchFieldValues.IMAGE_COUNT.from = from ?? null
											}
										}
										bind:to={
											() => staticSearchFieldValues.IMAGE_COUNT?.to,
											to => {
												staticSearchFieldValues.IMAGE_COUNT ??= { from: null, to: null }
												staticSearchFieldValues.IMAGE_COUNT.to = to ?? null
											}
										}
										isPinned={pinnedSearchFields.has(searchField.key)}
									/>
								</EditModeControls>
							{:else if searchField.key === 'HAS_PUBLIC_IMAGES'}
								<EditModeControls
									{searchField}
									{inEditMode}
									{pinnedSearchFields}
									{selectedSearchFieldsKeys}
								>
									<NullableRadioCheckbox
										id={searchField.key}
										label={searchField.name}
										bind:value={staticSearchFieldValues[searchField.key]}
									>
										{#snippet hint()}
											<Pin isPinned={pinnedSearchFields.has(searchField.key)} />
										{/snippet}
									</NullableRadioCheckbox>
								</EditModeControls>
							{:else}
								<p>{translate('vehicleSearch.unsupportedFieldType', 'Unsupported field type')}: {searchField.key}</p>
							{/if}
						{/if}
					{/each}
				</div>
			{:else}
				<div class="jumbotron my-3">
					<h1 class="display-4">{translate('vehicleSearch.noSelectedSearchFilters', 'No selected search filters.')}</h1>
					<p class="lead">
						{translate('partSearch.useTheAddSearchFilterDropdownBelow', 'Use the "Add Search Filter" dropdown below.')}
					</p>
				</div>
			{/if}
		</form>
	</div>

	<div class="card-footer border-bottom">
		<AddEditSearchFilters
			label={translate('common:inventory', 'Inventory')}
			{searchFieldsByCategory}
			{searchFields}
			{inEditMode}
			{pinnedSearchFields}
			{selectedSearchFieldsKeys}
			{toggleEditMode}
			clear={() => {
				// Clear Results / URL
				asr.go('app.part-search', {}, { inherit: false })
				// Clear Values
				staticSearchFieldValues = klona(getDefaultStaticSearchFieldValues())
				additionalFieldValues = {}
				typeFieldValues = {}
				inventoryQuestionValues = {}
				// DO NOT REMOVE unpinned search fields
			}}
		/>
	</div>
</div>

<uiView></uiView>
